import { useQueryClient } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useViewerQuery, useViewerStore } from '@/entities/viewer'
import { AccountBlockedPage } from '@/pages/account-blocked'
import { AppErrorPage } from '@/pages/app-error'
import { ReopenAppPage } from '@/pages/reopen-app'
import { API_ERROR_CODE } from '@/shared/api'

/**
 * Авторизация Mini App. Экрана входа нет — входить некуда: подпись Telegram
 * подставляет api-клиент, а здесь только реакция на её отказ.
 *
 * - Профиль (`/app/me`) грузится параллельно с первым экраном, а не перед
 *   ним: витрине он не нужен, а лишний круг до бэкенда на старте — это
 *   p95 первого экрана. Экраны, которым нужен профиль, ждут его сами.
 * - Отказ подписи (`TG-*`) на любом запросе заменяет все экраны: `TG-004` —
 *   «Откройте приложение заново», остальные — ошибка приложения.
 *   Автоповтора нет: та же `initData` даст тот же отказ (CLAUDE.md,
 *   «Авторизация»). Повтор — только по кнопке «Обновить экран»: при `TG-003`
 *   он помогает, если на бэкенде исправили токен бота.
 * - Блокировка пользователя (`U-003`) на любом запросе тоже заменяет все
 *   экраны — «Аккаунт заблокирован». Кнопки «Обновить» там нет: снять
 *   блокировку может только поддержка. Профиль перечитывается при возврате в
 *   приложение (`refetchOnWindowFocus`), так что после разблокировки экран
 *   уходит сам, без перезапуска.
 */
export const AuthGate = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient()
  const viewerQuery = useViewerQuery()
  const authError = useViewerStore((state) => state.authError)

  if (!authError) {
    return children
  }

  if (authError.code === API_ERROR_CODE.TG_EXPIRED) {
    return <ReopenAppPage error={authError} />
  }

  if (authError.code === API_ERROR_CODE.USER_BLOCKED) {
    return <AccountBlockedPage error={authError} />
  }

  const retry = async () => {
    // Успешный `/app/me` сам снимает отказ (см. `useViewerQuery`).
    const result = await viewerQuery.refetch()

    if (result.isSuccess) {
      // Упавшие на подписи запросы экранов — заново при монтировании, а не из кэша ошибки.
      await queryClient.resetQueries({ predicate: (query) => query.state.status === 'error' })
    }
  }

  return <AppErrorPage error={authError} onRetry={retry} />
}
